import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Bot,
  Upload,
  Download,
  RefreshCcw,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Settings,
  Play,
  Square,
  Image as ImageIcon,
  Calendar as CalendarIcon,
  FileSpreadsheet,
  TrendingUp,
  Wallet,
  History,
  Clock,
  Shield,
  LogIn,
} from 'lucide-react';
import { toast } from 'sonner';

// API URL helper
const getApiUrl = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
  const isPreview = hostname.includes('preview.emergentagent.com');
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  return isPreview ? '' : envUrl;
};
const API_URL = getApiUrl();

// Data fetch types
const FETCH_TYPES = {
  aum: {
    label: 'AUM (Equity)',
    icon: TrendingUp,
    description: 'Fetch client holding data from India Advantage backoffice',
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
  },
  brokerage: {
    label: 'Brokerage Data',
    icon: Wallet,
    description: 'Fetch franchise brokerage report (YTD)',
    color: 'text-[#C5A059]',
    bgColor: 'bg-[#C5A059]/10',
  },
};

// Session states
const SESSION_STATES = {
  IDLE: 'IDLE',
  STARTING: 'STARTING',
  NAVIGATING: 'NAVIGATING',
  WAITING_CAPTCHA: 'WAITING_CAPTCHA',
  LOGGING_IN: 'LOGGING_IN',
  LOGGED_IN: 'LOGGED_IN',
  FETCHING: 'FETCHING',
  COMPLETED: 'COMPLETED',
  ERROR: 'ERROR',
  READY_FOR_MANUAL: 'READY_FOR_MANUAL',
};

const DataFetchPage = () => {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('automated');
  const [fetchType, setFetchType] = useState('aum');
  
  // Session state
  const [sessionState, setSessionState] = useState(SESSION_STATES.IDLE);
  const [sessionId, setSessionId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  
  // CAPTCHA
  const [captchaImage, setCaptchaImage] = useState(null);
  const [pageScreenshot, setPageScreenshot] = useState(null);
  const [captchaValue, setCaptchaValue] = useState('');
  const [showCaptchaDialog, setShowCaptchaDialog] = useState(false);
  
  // Form inputs
  const [aumDate, setAumDate] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  });
  const [brokerageFromDate, setBrokerageFromDate] = useState(() => {
    const now = new Date();
    const fyYear = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
    return `${fyYear}-04-01`;
  });
  const [brokerageToDate, setBrokerageToDate] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  });
  
  // Credentials
  const [showCredentials, setShowCredentials] = useState(false);
  const [credentials, setCredentials] = useState({
    login_url: '',
    username: '',
    password: '',
    brokerage_password: '',
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [refreshingCaptcha, setRefreshingCaptcha] = useState(false);
  
  // Results
  const [fetchResult, setFetchResult] = useState(null);
  const [fetchLogs, setFetchLogs] = useState([]);
  
  // Load credentials
  useEffect(() => {
    const loadCredentials = async () => {
      try {
        const res = await fetch(`${API_URL}/api/data-fetch/credentials`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setCredentials(data);
        }
      } catch (err) {
        console.error('Error loading credentials:', err);
      }
    };
    
    if (token) {
      loadCredentials();
    }
  }, [token]);
  
  // Load fetch logs
  const loadFetchLogs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/data-fetch/logs?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setFetchLogs(data);
      }
    } catch (err) {
      console.error('Error loading fetch logs:', err);
    }
  }, [token]);
  
  useEffect(() => {
    if (token) {
      loadFetchLogs();
    }
  }, [loadFetchLogs, token]);
  
  // Save credentials
  const handleSaveCredentials = async () => {
    try {
      const res = await fetch(`${API_URL}/api/data-fetch/credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(credentials),
      });
      
      if (res.ok) {
        toast.success('Credentials saved successfully');
        setShowCredentials(false);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to save credentials');
      }
    } catch (err) {
      toast.error('Error saving credentials');
    }
  };
  
  // Start automated fetch session
  const startFetchSession = async () => {
    setSessionState(SESSION_STATES.STARTING);
    setProgress(10);
    setStatusMessage('Starting browser session...');
    setFetchResult(null);
    
    try {
      // Step 1: Start session
      const startRes = await fetch(`${API_URL}/api/data-fetch/start-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      
      const startData = await startRes.json();
      
      if (!startData.success) {
        throw new Error(startData.error || 'Failed to start session');
      }
      
      setSessionId(startData.session_id);
      setProgress(25);
      setStatusMessage('Navigating to login page...');
      setSessionState(SESSION_STATES.NAVIGATING);
      
      // Step 2: Navigate to login
      const navRes = await fetch(`${API_URL}/api/data-fetch/navigate-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ session_id: startData.session_id }),
      });
      
      const navData = await navRes.json();
      
      if (!navData.success) {
        throw new Error(navData.error || 'Failed to navigate to login');
      }
      
      setProgress(40);
      
      // Store screenshots for fallback
      if (navData.captcha_image) {
        setCaptchaImage(navData.captcha_image);
      }
      if (navData.page_screenshot) {
        setPageScreenshot(navData.page_screenshot);
      }
      
      // Step 3: Try auto-login (AI reads CAPTCHA automatically)
      setStatusMessage('Reading CAPTCHA automatically...');
      setSessionState(SESSION_STATES.LOGGING_IN);
      setProgress(50);
      
      const autoLoginRes = await fetch(`${API_URL}/api/data-fetch/auto-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          session_id: startData.session_id,
          max_attempts: 5
        }),
      });
      
      const autoLoginData = await autoLoginRes.json();
      
      if (autoLoginData.success) {
        // Auto-login successful! Navigate to report page
        setProgress(60);
        setSessionState(SESSION_STATES.LOGGED_IN);
        setStatusMessage(`Login successful (auto-read in ${autoLoginData.attempts || 1} attempt(s))! Navigating to report page...`);
        toast.success('Auto-login successful!');
        
        if (autoLoginData.page_screenshot) {
          setPageScreenshot(autoLoginData.page_screenshot);
        }
        
        // Navigate to the report page
        setProgress(70);
        setStatusMessage('Navigating to report page...');
        
        const navToReportRes = await fetch(`${API_URL}/api/data-fetch/navigate-to-report`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ 
            session_id: startData.session_id,
            report_type: fetchType === 'aum' ? 'aum' : 'brokerage'
          }),
        });
        
        const navToReportData = await navToReportRes.json();
        
        if (navToReportData.success) {
          setProgress(100);
          setSessionState(SESSION_STATES.READY_FOR_MANUAL);
          setStatusMessage('Report page is ready. Please enter dates and download manually on the page.');
          
          if (navToReportData.page_screenshot) {
            setPageScreenshot(navToReportData.page_screenshot);
          }
          
          toast.success('Report page ready! Enter dates and download manually.');
        } else {
          // Still show the page, user can continue manually
          setProgress(80);
          setStatusMessage('Partially navigated. You may need to navigate to the report page manually.');
          
          if (navToReportData.page_screenshot) {
            setPageScreenshot(navToReportData.page_screenshot);
          }
        }
      } else {
        // Auto-login failed, fall back to manual CAPTCHA entry
        console.log('Auto-login failed, falling back to manual entry:', autoLoginData.error);
        setStatusMessage('Auto-read failed. Please enter CAPTCHA manually.');
        setSessionState(SESSION_STATES.WAITING_CAPTCHA);
        setShowCaptchaDialog(true);
        
        if (autoLoginData.page_screenshot) {
          setPageScreenshot(autoLoginData.page_screenshot);
        }
      }
      
    } catch (err) {
      console.error('Fetch session error:', err);
      toast.error(err.message || 'Failed to start fetch session');
      setSessionState(SESSION_STATES.ERROR);
      setStatusMessage(err.message);
      await endSession();
    }
  };
  
  // Refresh CAPTCHA
  const refreshCaptcha = async () => {
    if (!sessionId) return;
    
    setRefreshingCaptcha(true);
    try {
      const res = await fetch(`${API_URL}/api/data-fetch/refresh-captcha`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ session_id: sessionId }),
      });
      
      const data = await res.json();
      
      if (data.success && data.page_screenshot) {
        setPageScreenshot(data.page_screenshot);
        toast.success(data.message || 'CAPTCHA refreshed');
      } else {
        toast.error(data.error || 'Failed to refresh CAPTCHA');
      }
    } catch (err) {
      toast.error('Error refreshing CAPTCHA');
      console.error('Refresh CAPTCHA error:', err);
    } finally {
      setRefreshingCaptcha(false);
    }
  };
  
  // Submit CAPTCHA and login
  const submitCaptcha = async () => {
    if (!captchaValue || captchaValue.length < 4) {
      toast.error('Please enter the 4-digit CAPTCHA');
      return;
    }
    
    setShowCaptchaDialog(false);
    setSessionState(SESSION_STATES.LOGGING_IN);
    setProgress(50);
    setStatusMessage('Logging in...');
    
    try {
      const loginRes = await fetch(`${API_URL}/api/data-fetch/submit-captcha`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          captcha_value: captchaValue,
        }),
      });
      
      const loginData = await loginRes.json();
      
      if (!loginData.success) {
        // Show the error screenshot if available
        if (loginData.page_screenshot) {
          setPageScreenshot(loginData.page_screenshot);
        }
        throw new Error(loginData.error || 'Login failed');
      }
      
      setProgress(60);
      setSessionState(SESSION_STATES.LOGGED_IN);
      setStatusMessage('Login successful! Fetching data...');
      
      // Proceed to fetch data
      await fetchData();
      
    } catch (err) {
      console.error('Login error:', err);
      toast.error(err.message || 'Login failed');
      setSessionState(SESSION_STATES.ERROR);
      setStatusMessage(err.message);
    }
  };
  
  // Fetch the actual data
  const fetchData = async (overrideSessionId = null) => {
    const currentSessionId = overrideSessionId || sessionId;
    
    setSessionState(SESSION_STATES.FETCHING);
    setProgress(70);
    
    try {
      let fetchRes;
      
      if (fetchType === 'aum') {
        // Format date as DD/MM/YYYY for the backend
        const [year, month, day] = aumDate.split('-');
        const formattedDate = `${day}/${month}/${year}`;
        
        setStatusMessage(`Fetching AUM data for ${formattedDate}... This may take 7-10 minutes.`);
        
        fetchRes = await fetch(`${API_URL}/api/data-fetch/fetch-aum`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            session_id: currentSessionId,
            as_on_date: formattedDate,
          }),
        });
      } else {
        // Format dates as DD/MM/YYYY
        const [fromYear, fromMonth, fromDay] = brokerageFromDate.split('-');
        const [toYear, toMonth, toDay] = brokerageToDate.split('-');
        const formattedFromDate = `${fromDay}/${fromMonth}/${fromYear}`;
        const formattedToDate = `${toDay}/${toMonth}/${toYear}`;
        
        setStatusMessage(`Fetching Brokerage data from ${formattedFromDate} to ${formattedToDate}...`);
        
        fetchRes = await fetch(`${API_URL}/api/data-fetch/fetch-brokerage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            session_id: currentSessionId,
            from_date: formattedFromDate,
            to_date: formattedToDate,
          }),
        });
      }
      
      const fetchData = await fetchRes.json();
      
      if (fetchData.page_screenshot) {
        setPageScreenshot(fetchData.page_screenshot);
      }
      
      if (!fetchData.success) {
        throw new Error(fetchData.error || 'Failed to fetch data');
      }
      
      setProgress(100);
      setSessionState(SESSION_STATES.COMPLETED);
      setStatusMessage('Data fetched successfully!');
      setFetchResult(fetchData);
      
      toast.success(`${fetchType === 'aum' ? 'AUM' : 'Brokerage'} data downloaded successfully`);
      
      // Refresh logs
      await loadFetchLogs();
      
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error(err.message || 'Failed to fetch data');
      setSessionState(SESSION_STATES.ERROR);
      setStatusMessage(err.message);
    } finally {
      // End the session
      await endSession();
    }
  };
  
  // End session
  const endSession = async () => {
    if (sessionId) {
      try {
        await fetch(`${API_URL}/api/data-fetch/end-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ session_id: sessionId }),
        });
      } catch (err) {
        console.error('Error ending session:', err);
      }
    }
    setSessionId(null);
  };
  
  // Cancel session
  const cancelSession = async () => {
    setShowCaptchaDialog(false);
    await endSession();
    setSessionState(SESSION_STATES.IDLE);
    setProgress(0);
    setStatusMessage('');
    setCaptchaValue('');
    setCaptchaImage(null);
    setPageScreenshot(null);
  };
  
  // Reset state
  const resetState = () => {
    setSessionState(SESSION_STATES.IDLE);
    setProgress(0);
    setStatusMessage('');
    setCaptchaValue('');
    setCaptchaImage(null);
    setPageScreenshot(null);
    setFetchResult(null);
    setSessionId(null);
  };
  
  // Download file
  const downloadFile = async () => {
    if (fetchResult?.file_name) {
      try {
        // Use fetch with auth header instead of window.open
        const response = await fetch(`${API_URL}/api/data-fetch/download/${sessionId}/${fetchResult.file_name}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        if (!response.ok) {
          throw new Error('Download failed');
        }
        
        // Create blob and download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fetchResult.file_name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('File downloaded successfully!');
      } catch (err) {
        console.error('Download error:', err);
        toast.error('Failed to download file');
      }
    }
  };
  
  // Render status indicator
  const renderStatusIndicator = () => {
    const stateConfig = {
      [SESSION_STATES.IDLE]: { icon: Play, color: 'text-slate-400', label: 'Ready to fetch' },
      [SESSION_STATES.STARTING]: { icon: Loader2, color: 'text-blue-400', label: 'Starting...', animate: true },
      [SESSION_STATES.NAVIGATING]: { icon: Loader2, color: 'text-blue-400', label: 'Navigating...', animate: true },
      [SESSION_STATES.WAITING_CAPTCHA]: { icon: Shield, color: 'text-amber-400', label: 'Waiting for CAPTCHA' },
      [SESSION_STATES.LOGGING_IN]: { icon: Loader2, color: 'text-blue-400', label: 'Logging in...', animate: true },
      [SESSION_STATES.LOGGED_IN]: { icon: CheckCircle2, color: 'text-emerald-400', label: 'Logged in' },
      [SESSION_STATES.FETCHING]: { icon: Loader2, color: 'text-[#C5A059]', label: 'Fetching data...', animate: true },
      [SESSION_STATES.COMPLETED]: { icon: CheckCircle2, color: 'text-emerald-400', label: 'Completed' },
      [SESSION_STATES.ERROR]: { icon: XCircle, color: 'text-red-400', label: 'Error' },
    };
    
    const config = stateConfig[sessionState];
    const Icon = config.icon;
    
    return (
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 ${config.color} ${config.animate ? 'animate-spin' : ''}`} />
        <span className={config.color}>{config.label}</span>
      </div>
    );
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Data Fetch</h1>
          <p className="text-slate-400 mt-1">
            Automated and manual data import from India Advantage backoffice
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCredentials(!showCredentials)}
            className="border-white/10"
            data-testid="credentials-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Credentials
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={loadFetchLogs}
            className="border-white/10"
            data-testid="refresh-logs-btn"
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Credentials Panel */}
      {showCredentials && (
        <Card className="card-dark border-amber-500/30 bg-amber-500/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-400" />
              Data Fetch Credentials
            </CardTitle>
            <CardDescription>
              Configure login credentials for India Advantage backoffice
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Login URL</Label>
                <Input
                  value={credentials.login_url}
                  onChange={(e) => setCredentials({ ...credentials, login_url: e.target.value })}
                  placeholder="https://backoffice.indiaadvantage.co.in/Account/Login"
                  className="bg-slate-800 border-white/10"
                  data-testid="credential-url"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Username</Label>
                <Input
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  placeholder="HDM"
                  className="bg-slate-800 border-white/10"
                  data-testid="credential-username"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Login Password</Label>
                <div className="relative">
                  <Input
                    type={showPasswords ? 'text' : 'password'}
                    value={credentials.password}
                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    placeholder="********"
                    className="bg-slate-800 border-white/10 pr-10"
                    data-testid="credential-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Brokerage Report Password</Label>
                <div className="relative">
                  <Input
                    type={showPasswords ? 'text' : 'password'}
                    value={credentials.brokerage_password}
                    onChange={(e) => setCredentials({ ...credentials, brokerage_password: e.target.value })}
                    placeholder="********"
                    className="bg-slate-800 border-white/10 pr-10"
                    data-testid="credential-brokerage-password"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setShowCredentials(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveCredentials}
                className="bg-[#C5A059] hover:bg-[#b4914e]"
                data-testid="save-credentials-btn"
              >
                Save Credentials
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-800/50 border border-white/5 p-1">
          <TabsTrigger
            value="automated"
            className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-white"
            data-testid="tab-automated"
          >
            <Bot className="w-4 h-4 mr-2" />
            Automated Fetch
          </TabsTrigger>
          <TabsTrigger
            value="manual"
            className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-white"
            data-testid="tab-manual"
          >
            <Upload className="w-4 h-4 mr-2" />
            Manual Upload
          </TabsTrigger>
          <TabsTrigger
            value="history"
            className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-white"
            data-testid="tab-history"
          >
            <History className="w-4 h-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>
        
        {/* Automated Fetch Tab */}
        <TabsContent value="automated" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Fetch Type & Configuration */}
            <div className="lg:col-span-2 space-y-6">
              {/* Fetch Type Selection */}
              <Card className="card-dark border-white/5">
                <CardHeader>
                  <CardTitle className="text-white">Select Data Type</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(FETCH_TYPES).map(([key, config]) => {
                      const Icon = config.icon;
                      const isActive = fetchType === key;
                      return (
                        <button
                          key={key}
                          onClick={() => setFetchType(key)}
                          disabled={sessionState !== SESSION_STATES.IDLE && sessionState !== SESSION_STATES.COMPLETED && sessionState !== SESSION_STATES.ERROR}
                          className={`p-6 rounded-lg border transition-all text-left ${
                            isActive
                              ? 'border-[#C5A059] bg-[#C5A059]/10'
                              : 'border-white/10 hover:border-white/20 hover:bg-white/5'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                          data-testid={`fetch-type-${key}`}
                        >
                          <Icon className={`w-8 h-8 mb-3 ${isActive ? 'text-[#C5A059]' : 'text-slate-400'}`} />
                          <p className={`text-lg font-semibold ${isActive ? 'text-white' : 'text-slate-300'}`}>
                            {config.label}
                          </p>
                          <p className="text-sm text-slate-400 mt-1">{config.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
              
              {/* Date Configuration */}
              <Card className="card-dark border-white/5">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-[#C5A059]" />
                    {fetchType === 'aum' ? 'AUM Date Configuration' : 'Brokerage Date Configuration'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {fetchType === 'aum' ? (
                    <div className="space-y-2">
                      <Label className="text-slate-300">As On Date (Holdings date - must be prior to today)</Label>
                      <Input
                        type="date"
                        value={aumDate}
                        onChange={(e) => setAumDate(e.target.value)}
                        max={new Date(Date.now() - 86400000).toISOString().split('T')[0]}
                        className="bg-slate-800 border-white/10 max-w-xs"
                        disabled={sessionState !== SESSION_STATES.IDLE && sessionState !== SESSION_STATES.COMPLETED && sessionState !== SESSION_STATES.ERROR}
                        data-testid="aum-date-input"
                      />
                      <p className="text-xs text-amber-400 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Note: AUM data download may take 7-10 minutes
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">From Date (Financial Year Start)</Label>
                        <Input
                          type="date"
                          value={brokerageFromDate}
                          onChange={(e) => setBrokerageFromDate(e.target.value)}
                          className="bg-slate-800 border-white/10"
                          disabled={sessionState !== SESSION_STATES.IDLE && sessionState !== SESSION_STATES.COMPLETED && sessionState !== SESSION_STATES.ERROR}
                          data-testid="brokerage-from-date"
                        />
                        <p className="text-xs text-slate-400">
                          Default: 01/04/YYYY (Beginning of FY)
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">To Date (Prior to today)</Label>
                        <Input
                          type="date"
                          value={brokerageToDate}
                          onChange={(e) => setBrokerageToDate(e.target.value)}
                          max={new Date(Date.now() - 86400000).toISOString().split('T')[0]}
                          className="bg-slate-800 border-white/10"
                          disabled={sessionState !== SESSION_STATES.IDLE && sessionState !== SESSION_STATES.COMPLETED && sessionState !== SESSION_STATES.ERROR}
                          data-testid="brokerage-to-date"
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Action & Progress */}
              <Card className="card-dark border-white/5">
                <CardContent className="pt-6 space-y-4">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    {renderStatusIndicator()}
                    {sessionState !== SESSION_STATES.IDLE && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={cancelSession}
                        className="text-red-400 hover:text-red-300"
                        data-testid="cancel-session-btn"
                      >
                        <Square className="w-4 h-4 mr-1" />
                        Cancel
                      </Button>
                    )}
                  </div>
                  
                  {/* Progress */}
                  {progress > 0 && (
                    <div className="space-y-2">
                      <Progress value={progress} className="h-2" />
                      <p className="text-sm text-slate-400">{statusMessage}</p>
                    </div>
                  )}
                  
                  {/* Action Button */}
                  {(sessionState === SESSION_STATES.IDLE || sessionState === SESSION_STATES.COMPLETED || sessionState === SESSION_STATES.ERROR) && (
                    <Button
                      onClick={sessionState === SESSION_STATES.COMPLETED || sessionState === SESSION_STATES.ERROR ? resetState : startFetchSession}
                      className="w-full bg-[#C5A059] hover:bg-[#b4914e] h-12 text-lg"
                      data-testid="start-fetch-btn"
                    >
                      {sessionState === SESSION_STATES.COMPLETED || sessionState === SESSION_STATES.ERROR ? (
                        <>
                          <RefreshCcw className="w-5 h-5 mr-2" />
                          Start New Fetch
                        </>
                      ) : (
                        <>
                          <Bot className="w-5 h-5 mr-2" />
                          Start Automated Fetch
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Result */}
                  {fetchResult && fetchResult.success && (
                    <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 space-y-3">
                      <div className="flex items-center gap-2 text-emerald-400">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="font-medium">Download Complete</span>
                      </div>
                      <p className="text-sm text-slate-300">
                        File: <span className="font-mono text-white">{fetchResult.file_name}</span>
                      </p>
                      <div className="flex gap-3">
                        <Button
                          onClick={downloadFile}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                          data-testid="download-file-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download File
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => window.location.href = '/import'}
                          className="flex-1 border-white/10"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Go to Import
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Right: Preview & Instructions */}
            <div className="space-y-6">
              {/* Page Preview */}
              {pageScreenshot && (
                <Card className="card-dark border-white/5">
                  <CardHeader>
                    <CardTitle className="text-white text-sm flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-[#C5A059]" />
                      Page Preview
                      {sessionState === SESSION_STATES.READY_FOR_MANUAL && (
                        <span className="ml-2 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">
                          Ready for Manual Input
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {sessionState === SESSION_STATES.READY_FOR_MANUAL && (
                      <div className="mb-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <p className="text-green-400 font-medium mb-2">✓ Report page is ready!</p>
                        <p className="text-slate-300 text-sm">
                          The browser is now on the report page. Please interact with the page directly in the external website to:
                        </p>
                        <ul className="text-slate-300 text-sm mt-2 ml-4 list-disc">
                          <li>Enter the date range (From Date and To Date)</li>
                          <li>Select report type (COMBINESEG)</li>
                          <li>Click the Excel download button</li>
                        </ul>
                        <p className="text-slate-400 text-xs mt-3">
                          Note: Open the backoffice website in another browser tab to interact with it.
                        </p>
                      </div>
                    )}
                    <div className="rounded-lg overflow-hidden border border-white/10">
                      <img
                        src={`data:image/png;base64,${pageScreenshot}`}
                        alt="Page preview"
                        className="w-full h-auto"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Instructions */}
              <Card className="card-dark border-white/5">
                <CardHeader>
                  <CardTitle className="text-white text-sm">How it works</CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-3 text-sm">
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#C5A059]/20 text-[#C5A059] flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                      <span className="text-slate-300">Select data type (AUM or Brokerage) and configure dates</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#C5A059]/20 text-[#C5A059] flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                      <span className="text-slate-300">Click &quot;Start Automated Fetch&quot; to begin</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#C5A059]/20 text-[#C5A059] flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                      <span className="text-slate-300">Enter the 4-digit CAPTCHA shown on the login page</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#C5A059]/20 text-[#C5A059] flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
                      <span className="text-slate-300">Wait for the download to complete (AUM: 7-10 mins)</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-[#C5A059]/20 text-[#C5A059] flex items-center justify-center flex-shrink-0 text-xs font-bold">5</span>
                      <span className="text-slate-300">Download and import the file via Data Import page</span>
                    </li>
                  </ol>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        {/* Manual Upload Tab */}
        <TabsContent value="manual">
          <Card className="card-dark border-white/5">
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <Upload className="w-16 h-16 text-slate-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Manual File Upload</h3>
                <p className="text-slate-400 mb-6">
                  Use the Data Import page for manual file uploads
                </p>
                <Button
                  onClick={() => window.location.href = '/import'}
                  className="bg-[#C5A059] hover:bg-[#b4914e]"
                  data-testid="go-to-import-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Go to Data Import
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* History Tab */}
        <TabsContent value="history">
          <Card className="card-dark border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <History className="w-5 h-5 text-[#C5A059]" />
                Fetch Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent>
              {fetchLogs.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No fetch activity yet</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {fetchLogs.map((log, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/50 border border-white/5"
                    >
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        log.action.includes('completed') ? 'bg-emerald-500' :
                        log.action.includes('started') ? 'bg-blue-500' :
                        log.action.includes('error') || log.action.includes('failed') ? 'bg-red-500' :
                        'bg-slate-500'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium">
                          {log.action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </p>
                        {log.details && (
                          <p className="text-xs text-slate-400 mt-1">
                            {Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                          </p>
                        )}
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* CAPTCHA Dialog */}
      <Dialog open={showCaptchaDialog} onOpenChange={setShowCaptchaDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#C5A059]" />
              Enter Login Credentials & CAPTCHA
            </DialogTitle>
            <DialogDescription>
              View the login page below and enter the CAPTCHA code shown
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Credentials Display */}
            <div className="grid grid-cols-2 gap-3 p-3 bg-slate-800/50 rounded-lg border border-white/10">
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">User Name (auto-filled)</Label>
                <div className="text-white font-medium">{credentials.username || 'Not set'}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Password (auto-filled)</Label>
                <div className="text-white font-medium">{'•'.repeat(8)}</div>
              </div>
            </div>
            
            {/* Full Page Screenshot - This is the main view */}
            {pageScreenshot && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[#C5A059] font-medium">Login Page (find CAPTCHA code below)</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={refreshCaptcha}
                    disabled={refreshingCaptcha}
                    className="border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059]/10"
                  >
                    <RefreshCcw className={`w-4 h-4 mr-1 ${refreshingCaptcha ? 'animate-spin' : ''}`} />
                    {refreshingCaptcha ? 'Refreshing...' : 'Refresh CAPTCHA'}
                  </Button>
                </div>
                <div className="rounded-lg overflow-hidden border-2 border-[#C5A059] bg-white">
                  <img
                    src={`data:image/png;base64,${pageScreenshot}`}
                    alt="Login page with CAPTCHA"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            )}
            
            {/* CAPTCHA Input */}
            <div className="space-y-2 p-4 bg-slate-800/50 rounded-lg border border-[#C5A059]">
              <Label className="text-white text-lg">Enter CAPTCHA Code (4 digits)</Label>
              <p className="text-slate-400 text-sm">Look at the login page above and find the distorted code image</p>
              <Input
                type="text"
                value={captchaValue}
                onChange={(e) => setCaptchaValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="Enter 4-digit code"
                className="bg-slate-900 border-[#C5A059] text-center text-3xl tracking-widest font-mono h-14"
                maxLength={4}
                autoFocus
                data-testid="captcha-input"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={cancelSession} className="text-slate-400">
              Cancel
            </Button>
            <Button
              onClick={submitCaptcha}
              disabled={captchaValue.length < 4}
              className="bg-[#C5A059] hover:bg-[#b4914e] text-black font-medium"
              data-testid="submit-captcha-btn"
            >
              <LogIn className="w-4 h-4 mr-2" />
              Submit & Login
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataFetchPage;
